import { Badge } from "@/components/ui/badge";
import { amountOf, isOverdue } from "@/lib/billing";
import { formatCurrency } from "@/lib/format";
import type { SellerBilling } from "@/types";

/**
 * As células de valor e de situação de uma fatura, iguais nas duas tabelas de
 * faturas (a do mês, em Faturamento, e a do vendedor). Separadas para as duas
 * não contarem a mesma fatura de jeitos diferentes.
 */

export function BillingAmount({ billing }: { billing: SellerBilling }) {
  return (
    <>
      {formatCurrency(amountOf(billing))}
      {billing.expiredDebt ? (
        <span className="block text-caption font-normal text-state-warning">
          inclui {formatCurrency(billing.expiredDebt)} de meses anteriores
        </span>
      ) : null}
      {billing.waived ? (
        <span className="block text-caption font-normal text-muted-foreground">
          comissão de {formatCurrency(billing.waivedPlatformFee ?? 0)} ignorada
        </span>
      ) : null}
      {billing.platformCouponCredit && !billing.waived ? (
        <span className="block text-caption font-normal text-state-ok">
          já abate {formatCurrency(billing.platformCouponCredit)} de cupons do Zé Padoca
        </span>
      ) : null}
      {billing.platformCouponCreditExcess ? (
        <span className="block text-caption font-semibold text-state-warning">
          repassar {formatCurrency(billing.platformCouponCreditExcess)} ao estabelecimento
        </span>
      ) : null}
    </>
  );
}

export function BillingSituation({ billing }: { billing: SellerBilling }) {
  // Ignorada que ainda cobrava dívidas antigas pode ter sido paga de verdade:
  // aí ela é "Paga", com a comissão ignorada ao lado.
  const paidWithMoney =
    billing.paidManually === true || billing.pendingPayment?.status === "paid";
  const showsWaivedFee = billing.waived === true && (!billing.paid || paidWithMoney);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {billing.paid ? (
        billing.waived && !paidWithMoney ? (
          <Badge variant="muted">Ignorada</Badge>
        ) : (
          <Badge variant="ok">Paga</Badge>
        )
      ) : isOverdue(billing) ? (
        <Badge variant="error">Vencida</Badge>
      ) : (
        <Badge variant="warning">Em aberto</Badge>
      )}
      {showsWaivedFee ? <Badge variant="muted">Comissão ignorada</Badge> : null}
      {billing.waivedSource === "testPartner" ? (
        <Badge variant="outline">Parceiro de teste</Badge>
      ) : null}
      {billing.paidManually ? <Badge variant="muted">Baixa manual</Badge> : null}
      {billing.accumulatedIntoBillingId ? <Badge variant="outline">Absorvida</Badge> : null}
    </div>
  );
}
